import { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { getImageContentById } from "@helpers/general";

import type { RootState } from "@components/OrderTaking/Store";

export type KcalSetting = {
    isCalorieActive: boolean;
    // Resolved image src for the custom calorie icon, or "" when none is set
    // (callers treat "" as "fall back to the default FireIcon").
    customIcon: string;
};

// `calorieId` is attached to productSetting at runtime (see Commonfunction.js)
// but is absent from the slice's static initial state, so RootState's inferred
// shape doesn't carry it. Widen the selected slice here, in one place, instead
// of re-declaring an inline state type in every kcal component.
type ProductSetting = RootState["settingSlice"]["productSetting"] & {
    calorieId?: string;
};

// Single source of truth for the global calorie display setting consumed by
// every kcal surface (ProductCalorie, ComposedProductTotalKcal, …). Reading
// productSetting through RootState here means a rename of isCalorieActive
// fails the type-check once, not silently in each component.
export function useKcalSetting(): KcalSetting {
    const { isCalorieActive, calorieId } = useSelector(
        (state: RootState) => state.settingSlice.productSetting as ProductSetting,
        shallowEqual
    );

    const customIcon = useMemo(
        () => (calorieId ? getImageContentById(calorieId) : ""),
        [calorieId]
    );

    return { isCalorieActive: Boolean(isCalorieActive), customIcon };
}
